/* Studio Manager · Studios & Invites
   Three sections stacked top-to-bottom:
     1. "Invite a studio" form — manual entry: name + email + optional personal note.
     2. "Pending invites" table — newest first, with resend/revoke. Tokens are masked.
     3. "Active studios" roster — who's actually onboarded.

   Lives at /admin/studio-invites. Sidebar item label: "Studios". */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Mail, Send, RotateCcw, Trash2, Sparkles, CheckCircle2,
  Clock, XCircle, ChevronRight, Copy, RefreshCw, Settings, LogIn, Ban,
  PlayCircle, AlertTriangle, X, Globe,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { copyToClipboardWithToast } from "@/lib/clipboard";
import LumetierInsights from "@/components/LumetierInsights";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { EditorialList, EditorialRow, EditorialStatusPill, EditorialAction } from "@/components/admin/EditorialList";
import EditorialEmptyState from "@/components/editorial/EditorialEmptyState";
import GoldFoilButton from "@/components/editorial/GoldFoilButton";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch (err) {
    console.warn("studio-invites date parse failed", err);
    return iso;
  }
}

const STATUS_BADGES = {
  pending:  { label: "Pending",  cls: "bg-amber-100 text-amber-900",   Icon: Clock },
  accepted: { label: "Accepted", cls: "bg-emerald-100 text-emerald-900", Icon: CheckCircle2 },
  expired:  { label: "Expired",  cls: "bg-stone-200 text-stone-700",   Icon: XCircle },
  revoked:  { label: "Revoked",  cls: "bg-rose-100 text-rose-900",     Icon: XCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGES[status] || STATUS_BADGES.pending;
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] ${cfg.cls}`} data-testid={`invite-status-${status}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function studioPublicUrl(slug) {
  // Allure stays on bare paths for SEO. Every other tenant lives under /s/{slug}.
  if (slug === "allure-image") {
    return window.location.origin;
  }
  return `${window.location.origin}/s/${slug}`;
}

export default function AdminStudioInvites() {
  const [invites, setInvites] = useState([]);
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ studio_name: "", owner_name: "", owner_email: "", personal_note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [lastSent, setLastSent] = useState(null); // {email, studio_name}
  const [managing, setManaging] = useState(null); // studio object being managed

  async function loadAll() {
    setLoading(true);
    try {
      const [i, s] = await Promise.all([
        api.get("/admin/studio-invites"),
        api.get("/admin/studios"),
      ]);
      setInvites(i.data);
      setStudios(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.studio_name || !form.owner_name || !form.owner_email) {
      return toast.error("Studio name, owner name, and email are all required.");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/studio-invites", form);
      toast.success(`Invite emailed to ${data.owner_email}`);
      setLastSent({ email: data.owner_email, studio_name: data.studio_name });
      setForm({ studio_name: "", owner_name: "", owner_email: "", personal_note: "" });
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send invite");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend(invite) {
    try {
      await api.post(`/admin/studio-invites/${invite.id}/resend`);
      toast.success(`Resent to ${invite.owner_email}`);
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't resend");
    }
  }

  async function handleRevoke(invite) {
    if (!window.confirm(`Revoke the invite for ${invite.owner_email}? The magic link will stop working.`)) return;
    try {
      await api.delete(`/admin/studio-invites/${invite.id}`);
      toast.success("Revoked.");
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't revoke");
    }
  }

  const pendingCount = invites.filter((i) => i.effective_status === "pending").length;
  const acceptedCount = invites.filter((i) => i.effective_status === "accepted").length;

  return (
    <div className="space-y-8" data-testid="admin-studio-invites">
      <LumetierInsights />

      <AdminPageHeader
        breadcrumb={["LUMETIER", "STUDIOS"]}
        title="Studios & invitations"
        count={studios.length}
        rightSlot={
          <button
            onClick={loadAll}
            disabled={loading}
            data-testid="invites-refresh-btn"
            className="inline-flex items-center gap-2 h-9 px-3 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        }
      />
      <p className="text-sm text-muted-foreground -mt-4 max-w-xl">
        Manually invite other photographers to spin up their own studio on Lumetier. Each invite is a magic link valid for 14 days.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Active studios" value={studios.length} />
        <Stat label="Pending invites" value={pendingCount} />
        <Stat label="Accepted" value={acceptedCount} />
        <Stat label="Total invites" value={invites.length} />
      </div>

      {/* ----- Invite form ----- */}
      <section className="border-y border-[color:var(--gold-foil)]/30 py-8" data-testid="invite-form-section">
        <p className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] mb-3">
          Invite · New studio
        </p>
        <h2 className="text-3xl sm:text-4xl italic font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          Invite a new studio
        </h2>
        <p className="text-sm font-serif italic text-muted-foreground mb-6 max-w-xl">
          Each invite spins up a fresh studio with its own brand spine — magic-link valid for 14 days.
        </p>

        {lastSent && (
          <div className="mb-5 px-4 py-3 border border-[color:var(--gold-base)]/40 text-[color:var(--gold-base)] text-sm" data-testid="invite-success-banner">
            <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
            Invite sent to <strong>{lastSent.email}</strong> ({lastSent.studio_name}). Check the &quot;Pending invites&quot; table below to track status.
          </div>
        )}

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="invite-form">
          <Field
            label="Studio name"
            placeholder="Jane Smith Photography"
            value={form.studio_name}
            onChange={(v) => setForm((f) => ({ ...f, studio_name: v }))}
            testid="invite-studio-name"
            required
          />
          <Field
            label="Owner name"
            placeholder="Jane Smith"
            value={form.owner_name}
            onChange={(v) => setForm((f) => ({ ...f, owner_name: v }))}
            testid="invite-owner-name"
            required
          />
          <Field
            label="Owner email"
            type="email"
            placeholder="jane@studio.com"
            value={form.owner_email}
            onChange={(v) => setForm((f) => ({ ...f, owner_email: v }))}
            testid="invite-owner-email"
            required
            className="md:col-span-2"
          />
          <Field
            label="Personal note (optional)"
            placeholder="Hey Jane — thrilled to have you. Reach out anytime."
            value={form.personal_note}
            onChange={(v) => setForm((f) => ({ ...f, personal_note: v }))}
            testid="invite-personal-note"
            multiline
            className="md:col-span-2"
          />
          <div className="md:col-span-2 flex justify-end pt-2">
            <GoldFoilButton
              type="submit"
              disabled={submitting}
              data-testid="invite-submit-btn"
              tone="dark"
            >
              {submitting ? "Sending…" : "Send invite"}
            </GoldFoilButton>
          </div>
        </form>
      </section>

      {/* ----- Pending / past invites table ----- */}
      <section data-testid="invites-list-section">
        <h2 className="text-xl font-light italic mb-4" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          Invitations
        </h2>
        {loading ? (
          <div className="py-10 text-center text-[11px] uppercase tracking-[0.28em] text-muted-foreground" data-testid="invites-loading">Loading…</div>
        ) : invites.length === 0 ? (
          <EditorialEmptyState
            eyebrow="The book is empty"
            headline={<>No invitations <em>yet</em>.</>}
            description="Use the form above to invite your first studio."
            tone="charcoal"
            data-testid="invites-empty"
          />
        ) : (
          <EditorialList data-testid="invites-rows">
            {invites.map((i, idx) => (
              <EditorialRow
                key={i.id}
                index={idx}
                title={i.studio_name}
                meta={[
                  i.owner_name,
                  <a key="email" href={`mailto:${i.owner_email}`} className="hover:underline">
                    {i.owner_email}
                  </a>,
                  fmtDate(i.last_emailed_at || i.created_at),
                ]}
                status={<StatusBadge status={i.effective_status} />}
                actions={
                  <>
                    {(i.effective_status === "pending" || i.effective_status === "expired") && (
                      <button
                        onClick={() => handleResend(i)}
                        title="Resend invite email"
                        data-testid={`invite-resend-${i.id}`}
                        className="text-muted-foreground/70 hover:text-foreground transition-colors"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {i.effective_status === "pending" && (
                      <button
                        onClick={() => handleRevoke(i)}
                        title="Revoke"
                        data-testid={`invite-revoke-${i.id}`}
                        className="text-muted-foreground/70 hover:text-destructive transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </>
                }
                data-testid={`invite-row-${i.id}`}
              />
            ))}
          </EditorialList>
        )}
      </section>

      {/* ----- Active studios roster ----- */}
      <section data-testid="studios-list-section">
        <h2 className="text-xl font-light mb-3 inline-flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          <Building2 className="w-5 h-5" /> Active studios
        </h2>
        <p className="text-sm text-muted-foreground mb-3">
          Every studio on Lumetier — yours included. Allure is marked as the founding studio.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="studios-grid">
          {studios.map((s) => (
            <div
              key={s.id}
              className={`border p-5 transition-colors ${s.is_active === false ? "border-amber-500/40 bg-amber-500/5" : "border-[color:var(--gold-foil)]/25 hover:border-[color:var(--gold-foil)]/50"}`}
              data-testid={`studio-card-${s.slug}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="font-serif text-xl truncate flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                    {s.name}
                    {s.is_active === false && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.18em] bg-amber-200 text-amber-900"
                        data-testid={`studio-suspended-badge-${s.slug}`}
                      >
                        <Ban className="w-2.5 h-2.5" /> Suspended
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground/70 font-mono mt-1">/{s.slug}</div>
                </div>
                <PlanBadge plan={s.plan} />
              </div>
              <div className="grid grid-cols-2 gap-3 text-[10px] uppercase tracking-[0.28em] text-muted-foreground/70 border-t border-[color:var(--gold-foil)]/15 pt-3">
                <div><span className="block font-serif text-2xl text-foreground tabular-nums italic font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{s.booking_count}</span>Bookings</div>
                <div><span className="block font-serif text-2xl text-foreground tabular-nums italic font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{s.gallery_count}</span>Galleries</div>
              </div>
              <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-[color:var(--gold-foil)]/15">
                <div className="text-[11px] text-muted-foreground/70 truncate" title={studioPublicUrl(s.slug)}>
                  {studioPublicUrl(s.slug).replace(/^https?:\/\//, "")}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => copyToClipboardWithToast(studioPublicUrl(s.slug), "Public URL copied")}
                    title="Copy public booking URL"
                    data-testid={`studio-copy-url-${s.slug}`}
                    className="text-muted-foreground/70 hover:text-foreground transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <a
                    href={studioPublicUrl(s.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open public site"
                    data-testid={`studio-open-url-${s.slug}`}
                    className="text-muted-foreground/70 hover:text-foreground transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[color:var(--gold-foil)]/15">
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/60">Created {fmtDate(s.created_at)}</div>
                {s.plan !== "founder" && (
                  <EditorialAction
                    onClick={() => setManaging(s)}
                    data-testid={`studio-manage-${s.slug}`}
                    icon={<Settings className="w-3 h-3 mr-1" />}
                  >
                    Manage
                  </EditorialAction>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {managing && (
        <StudioManageDrawer
          studio={managing}
          onClose={() => setManaging(null)}
          onChanged={async () => {
            await loadAll();
            // Refresh `managing` from the just-reloaded list so the drawer
            // shows the latest data without closing.
            const fresh = (await api.get("/admin/studios")).data;
            const next = fresh.find((s) => s.id === managing.id);
            setStudios(fresh);
            setManaging(next || null);
          }}
        />
      )}
    </div>
  );
}

function PlanBadge({ plan }) {
  const map = {
    founder: { cls: "bg-amber-100 text-amber-900 border-amber-300", label: "Founder" },
    beta:    { cls: "bg-emerald-100 text-emerald-900 border-emerald-300", label: "Beta" },
    solo:    { cls: "bg-stone-100 text-stone-700 border-stone-300", label: "Solo" },
    studio:  { cls: "bg-stone-200 text-stone-800 border-stone-400", label: "Studio" },
    pro:     { cls: "bg-foreground text-background border-foreground", label: "Pro" },
  };
  const cfg = map[plan] || map.beta;
  return (
    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] border ${cfg.cls}`}>{cfg.label}</span>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[color:var(--gold-foil)]/30 bg-[color:var(--charcoal)] text-[color:var(--cream)] p-5 hover:border-[color:var(--gold-foil)]/60 transition-colors min-h-[120px] flex flex-col justify-between">
      <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)]">{label}</div>
      <div className="font-serif text-4xl font-light italic tabular-nums leading-none mt-4" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", required, multiline, testid, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] mb-2">
        {label} {required && <span className="text-[color:var(--gold-base)]/70">*</span>}
      </span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          rows={3}
          data-testid={testid}
          className="input"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          data-testid={testid}
          className="w-full bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground/60 transition-colors"
        />
      )}
    </label>
  );
}


/* --------------------------------------------------------------------------
   Tenant management drawer — edit + suspend + impersonate + delete

   Opens from the "Manage" button on any non-founder studio card. Slides in
   from the right (`fixed inset-y-0 right-0`). Backdrop click + Escape close.
   Four action sections, top to bottom:
     1. Edit (name, owner email, plan tier) — save button
     2. Suspend / Reactivate toggle (one click, no confirm — recoverable)
     3. Impersonate ("Open this studio's admin") — swaps localStorage token + redirect
     4. Delete — type-the-slug-to-confirm, cascade-deletes all tenant data
   -------------------------------------------------------------------------- */
function StudioManageDrawer({ studio, onClose, onChanged }) {
  const navigate = useNavigate();

  // Editable fields — local state so the form doesn't re-render the whole page
  const [name, setName] = useState(studio.name || "");
  const [ownerEmail, setOwnerEmail] = useState(studio.owner_email || "");
  const [plan, setPlan] = useState(studio.plan || "beta");
  const [saving, setSaving] = useState(false);

  // Custom-domain state — fetched separately from /admin/studios/{id}/site-summary
  // because the value lives in `site_settings`, not on the `studios` row.
  const [domainInfo, setDomainInfo] = useState(null);
  const [activating, setActivating] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);

  // Resync if the parent rehydrates the studio object (e.g. after a save)
  useEffect(() => {
    setName(studio.name || "");
    setOwnerEmail(studio.owner_email || "");
    setPlan(studio.plan || "beta");
  }, [studio]);

  // Fetch site-summary on open (and whenever the studio id changes) so we
  // know whether to show "Pending" or "Live" or hide the custom-domain block.
  useEffect(() => {
    let alive = true;
    api.get(`/admin/studios/${studio.id}/site-summary`)
      .then((r) => { if (alive) setDomainInfo(r.data); })
      .catch(() => { if (alive) setDomainInfo({ custom_domain_status: "none" }); });
    return () => { alive = false; };
  }, [studio.id]);

  async function setDomainStatus(nextStatus, nextDomain) {
    setActivating(true);
    try {
      const body = { status: nextStatus };
      if (nextDomain !== undefined) body.domain = nextDomain;
      const { data } = await api.patch(`/admin/studios/${studio.id}/custom-domain`, body);
      toast.success(
        nextStatus === "active" ? `Marked ${data.domain || "domain"} as Live`
        : nextStatus === "none" ? "Custom domain cleared"
        : "Custom domain marked pending",
      );
      setDomainInfo((prev) => ({
        ...(prev || {}),
        custom_domain_status: data.status,
        custom_domain_requested: data.domain ?? prev?.custom_domain_requested,
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update status");
    } finally {
      setActivating(false);
    }
  }

  // Esc closes the drawer
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const dirty = name !== studio.name || ownerEmail !== studio.owner_email || plan !== studio.plan;

  async function save() {
    if (!name.trim()) return toast.error("Studio name is required");
    setSaving(true);
    try {
      await api.patch(`/admin/studios/${studio.id}`, {
        name: name.trim(),
        owner_email: ownerEmail.trim().toLowerCase(),
        plan,
      });
      toast.success("Studio updated.");
      await onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save changes");
    } finally {
      setSaving(false);
    }
  }

  async function toggleSuspend() {
    const next = !studio.is_active;
    const verb = next ? "reactivate" : "suspend";
    if (!window.confirm(
      next
        ? `Reactivate ${studio.name}? The owner will be able to log in again.`
        : `Suspend ${studio.name}?\n\nThe owner will be locked out (login disabled) but their data stays intact. You can reactivate any time.`,
    )) return;
    try {
      await api.patch(`/admin/studios/${studio.id}`, { is_active: next });
      toast.success(`Studio ${verb}d.`);
      await onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Couldn't ${verb}`);
    }
  }

  async function impersonate() {
    if (!window.confirm(
      `Open ${studio.name}'s admin dashboard?\n\nYour current session will be replaced — sign back in afterwards to return to the platform owner view.`,
    )) return;
    try {
      const { data } = await api.post(`/admin/studios/${studio.id}/impersonate`);
      // Swap the JWT in localStorage — AuthContext picks it up on next page load.
      // We hard-navigate to /admin so the whole app re-mounts with the new identity.
      localStorage.setItem("allure_token", data.token);
      toast.success(`Now signed in as ${studio.name}`);
      // Brief delay so the toast is visible before the reload
      setTimeout(() => { window.location.href = "/admin"; }, 600);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't open this studio");
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end"
      onClick={onClose}
      data-testid="studio-manage-drawer-backdrop"
    >
      <aside
        className="bg-card border-l border-border w-full max-w-md h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="studio-manage-drawer"
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-start justify-between z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-1.5">Manage studio</div>
            <div className="text-xl font-light truncate" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              {studio.name}
            </div>
            <div className="text-xs text-muted-foreground font-mono">/{studio.slug}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="studio-manage-close"
            className="text-muted-foreground hover:text-foreground shrink-0"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-8">
          {/* 1. Edit form */}
          <section data-testid="studio-edit-section">
            <SectionLabel>Details</SectionLabel>
            <div className="space-y-4">
              <Field
                label="Studio name"
                value={name}
                onChange={setName}
                testid="manage-studio-name"
                required
              />
              <Field
                label="Owner email"
                type="email"
                value={ownerEmail}
                onChange={setOwnerEmail}
                testid="manage-owner-email"
              />
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">Plan tier</span>
                <select
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                  data-testid="manage-plan-select"
                  className="w-full bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground/60"
                >
                  <option value="beta">Beta (free)</option>
                  <option value="solo">Solo</option>
                  <option value="studio">Studio</option>
                  <option value="pro">Pro</option>
                </select>
              </label>
              <div className="flex justify-end">
                <button
                  onClick={save}
                  disabled={!dirty || saving}
                  data-testid="manage-save-btn"
                  className="inline-flex items-center gap-2 h-9 px-5 text-xs uppercase tracking-[0.22em] bg-foreground text-background hover:opacity-90 disabled:opacity-40"
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </section>

          {/* 2. Suspend / reactivate */}
          <section data-testid="studio-suspend-section">
            <SectionLabel>Access</SectionLabel>
            <div className={`border ${studio.is_active === false ? "border-amber-500/40 bg-amber-500/5" : "border-border"} p-4`}>
              <div className="flex items-start gap-3">
                {studio.is_active === false ? (
                  <Ban className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">
                    {studio.is_active === false ? "Suspended — owner cannot sign in" : "Active — owner can sign in"}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Suspending locks the owner out but preserves all their data. Reversible at any time.
                  </div>
                </div>
              </div>
              <button
                onClick={toggleSuspend}
                data-testid="manage-suspend-toggle"
                className={`mt-4 inline-flex items-center gap-2 h-9 px-4 text-xs uppercase tracking-[0.22em] ${
                  studio.is_active === false
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : "border border-amber-400 text-amber-900 hover:bg-amber-100"
                }`}
              >
                {studio.is_active === false ? (
                  <><PlayCircle className="w-3.5 h-3.5" /> Reactivate</>
                ) : (
                  <><Ban className="w-3.5 h-3.5" /> Suspend studio</>
                )}
              </button>
            </div>
          </section>

          {/* 3. Impersonate */}
          <section data-testid="studio-impersonate-section">
            <SectionLabel>Support</SectionLabel>
            <div className="border border-border p-4">
              <div className="flex items-start gap-3">
                <LogIn className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Open this studio&apos;s dashboard</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Sign in as the owner to see exactly what they see. Useful for triaging support tickets. Your current session ends — sign back in to return.
                  </div>
                </div>
              </div>
              <button
                onClick={impersonate}
                disabled={studio.is_active === false}
                data-testid="manage-impersonate-btn"
                className="mt-4 inline-flex items-center gap-2 h-9 px-4 text-xs uppercase tracking-[0.22em] border border-border hover:border-foreground/60 disabled:opacity-40 disabled:cursor-not-allowed"
                title={studio.is_active === false ? "Reactivate the studio first" : ""}
              >
                <LogIn className="w-3.5 h-3.5" /> Open as {studio.name.split(" ")[0]}
              </button>
            </div>
          </section>

          {/* 4. Custom domain — only render when the tenant has requested one. */}
          {domainInfo && domainInfo.custom_domain_status !== "none" && domainInfo.custom_domain_requested && (
            <section data-testid="studio-domain-section">
              <SectionLabel>Custom domain</SectionLabel>
              <div className={`border ${domainInfo.custom_domain_status === "active" ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"} p-4`}>
                <div className="flex items-start gap-3">
                  <Globe className={`w-5 h-5 ${domainInfo.custom_domain_status === "active" ? "text-emerald-400" : "text-amber-400"} shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">
                      {domainInfo.custom_domain_status === "active" ? "Live" : "Pending setup"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 break-all font-mono">
                      {domainInfo.custom_domain_requested}
                    </div>
                    <div className="text-xs text-muted-foreground/80 mt-2 leading-relaxed">
                      {domainInfo.custom_domain_status === "active"
                        ? "Tenant sees a green Live banner in their Branding tab."
                        : "Tenant sees an amber Pending banner. Configure DNS + SSL on Emergent, then mark Live below."}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {domainInfo.custom_domain_status === "requested" && (
                    <button
                      onClick={() => setDomainStatus("active")}
                      disabled={activating}
                      data-testid="domain-mark-active"
                      className="inline-flex items-center gap-2 h-9 px-4 text-xs uppercase tracking-[0.22em] bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Mark Live
                    </button>
                  )}
                  {domainInfo.custom_domain_status === "active" && (
                    <button
                      onClick={() => setDomainStatus("requested")}
                      disabled={activating}
                      data-testid="domain-mark-pending"
                      className="inline-flex items-center gap-2 h-9 px-4 text-xs uppercase tracking-[0.22em] border border-amber-400 text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                    >
                      <Clock className="w-3.5 h-3.5" /> Move back to Pending
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (!window.confirm("Remove this custom-domain request? Tenant will need to re-submit if they still want one.")) return;
                      setDomainStatus("none", "");
                    }}
                    disabled={activating}
                    data-testid="domain-clear"
                    className="inline-flex items-center gap-2 h-9 px-3 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                  >
                    <XCircle className="w-3.5 h-3.5" /> Clear
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* 5. Delete */}
          <section data-testid="studio-delete-section">
            <SectionLabel>Danger zone</SectionLabel>
            {!deleteOpen ? (
              <div className="border border-rose-500/40 bg-rose-500/5 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-rose-900">Delete this studio</div>
                    <div className="text-xs text-rose-800/80 mt-1">
                      Permanently removes the studio plus every booking, gallery, contract, invoice, customer, and team member. Cannot be undone.
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setDeleteOpen(true)}
                  data-testid="manage-delete-open"
                  className="mt-4 inline-flex items-center gap-2 h-9 px-4 text-xs uppercase tracking-[0.22em] border border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete studio…
                </button>
              </div>
            ) : (
              <DeleteConfirm
                studio={studio}
                onCancel={() => setDeleteOpen(false)}
                onDeleted={() => {
                  onClose();
                  onChanged();
                  navigate("/admin/studio-invites");
                }}
              />
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}

function DeleteConfirm({ studio, onCancel, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const matches = typed === studio.slug;

  async function go() {
    if (!matches) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/studios/${studio.id}`, {
        params: { confirm_slug: studio.slug },
      });
      const totalRows = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Deleted ${studio.name} (${totalRows} rows removed)`);
      onDeleted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="border border-rose-500/40 bg-rose-500/5 p-4" data-testid="manage-delete-confirm">
      <div className="text-sm font-medium text-rose-900 mb-2">
        Type <code className="font-mono bg-rose-100 px-1 py-0.5 text-xs">{studio.slug}</code> to confirm
      </div>
      <input
        type="text"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={studio.slug}
        autoFocus
        data-testid="manage-delete-confirm-input"
        className="w-full bg-background border border-rose-500/40 px-3 py-2 text-sm font-mono focus:outline-none focus:border-rose-500"
      />
      <div className="mt-4 flex items-center gap-2 justify-end">
        <button
          onClick={onCancel}
          className="h-9 px-3 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground"
          data-testid="manage-delete-cancel"
        >
          Cancel
        </button>
        <button
          onClick={go}
          disabled={!matches || deleting}
          data-testid="manage-delete-confirm-btn"
          className="inline-flex items-center gap-2 h-9 px-4 text-xs uppercase tracking-[0.22em] bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Trash2 className="w-3.5 h-3.5" /> {deleting ? "Deleting…" : "Permanently delete"}
        </button>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-3 font-medium">
      {children}
    </div>
  );
}
